// This is the main app component for the Insurance Frontend.
// It is used to render the app and the routes.
// It is used to render the app and the routes.

import React from 'react';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';
import { ThemeProvider as MuiThemeProvider } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import 'dayjs/locale/en-gb';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { DashboardProvider } from './contexts/DashboardContext';
import { AttributesProvider } from './contexts/AttributesContext';
import { ThemeProvider, useTheme } from './contexts/ThemeContext';
import Header from './components/common/Header';
import UpdateAvailableSnackbar from './components/common/UpdateAvailableSnackbar';
import DocsHandoffSnackbar from './components/common/DocsHandoffSnackbar';
import Agent from './pages/Agent';
import Data from './pages/Data';
import Settings from './pages/Settings';
import Dashboard from './pages/Dashboard';
import OrganisationSelector from './pages/OrganisationSelector';
import Restoring from './pages/Restoring';
import NotFound from './pages/NotFound';
import ApplicationError from './pages/ApplicationError';
import { TextFormatterProvider } from './components/common/TextFormatterProvider';
import { Box, Button } from '@mui/material';


function AuthenticationMessage({ children }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '100vh', p: 3 }}>
      {children}
    </Box>
  );
}

function PrivateRoute({ children }) {
  const {
    currentUserMain,
    currentUserPlatform,
    authInitialised,
    bridgeSigningIn,
    awaitingBridge,
    authError,
    companyName,
    tokenOrganisation,
    restoreStatus,
    retryRestoreCheck,
  } = useAuth();

  // awaitingBridge covers the gap between Firebase restoring a previous tab's
  // session and the Site's token bridge arriving — rendering from the stale
  // session in that gap flashes the wrong screen. Checked before
  // authInitialised so Site-opened tabs show one steady message throughout.
  if (bridgeSigningIn || awaitingBridge) {
    return <AuthenticationMessage>Signing you in to Decode...</AuthenticationMessage>;
  }

  if (!authInitialised) {
    return <AuthenticationMessage>Checking your Decode sign-in...</AuthenticationMessage>;
  }

  if (authError) {
    return (
      <AuthenticationMessage>
        <span>
          {authError}{' '}
          <a href="https://bimanalytics.ai">Return to BIM Analytics.</a>
        </span>
      </AuthenticationMessage>
    );
  }

  if (!currentUserMain || !currentUserPlatform) {
    return (
      <AuthenticationMessage>
        <span>
          Please open Decode from <a href="https://bimanalytics.ai">BIM Analytics</a>.
        </span>
      </AuthenticationMessage>
    );
  }

  // Check if user is an employee (organisation='all') and hasn't selected an organisation
  if (tokenOrganisation === 'all' && !companyName) {
    return <OrganisationSelector />;
  }

  // Workspace restore gate, failing closed: nothing renders until the lock has
  // been read, and a listener failure offers a retry rather than opening up.
  if (companyName && restoreStatus === 'unknown') {
    return <AuthenticationMessage>Checking your workspace...</AuthenticationMessage>;
  }
  if (companyName && restoreStatus === 'error') {
    return (
      <AuthenticationMessage>
        <span>
          Decode could not check whether your workspace is being restored.{' '}
          <Button size="small" onClick={retryRestoreCheck} sx={{ textTransform: 'none' }}>Try again</Button>
        </span>
      </AuthenticationMessage>
    );
  }
  if (restoreStatus === 'active') {
    return <Restoring />;
  }

  return companyName ? (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100vh', overflow: 'hidden' }}>
      <Header />
      <Box sx={{ 
        flexGrow: 1, 
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {children}
      </Box>
    </Box>
  ) : (
    <div>Please sign in to access this page.</div>
  );
}

const router = createBrowserRouter([{
  errorElement: <ApplicationError />,
  children: [
    { path: '/settings', element: <PrivateRoute><Settings /></PrivateRoute> },
    { path: '/', element: <PrivateRoute><Dashboard /></PrivateRoute> },
    { path: '/data', element: <PrivateRoute><Data /></PrivateRoute> },
    { path: '/agent', element: <PrivateRoute><Agent /></PrivateRoute> },
    { path: '/agent/:agentId', element: <PrivateRoute><Agent /></PrivateRoute> },
    // Any other address: a Decode page saying so, inside the signed-in shell, rather
    // than the router's own error boundary.
    { path: '*', element: <PrivateRoute><NotFound /></PrivateRoute> },
  ],
}]);

function AppContent() {
  const { theme } = useTheme();
  
  return (
    <MuiThemeProvider theme={theme}>
      {/* Every customer is UK-based, so the locale is fixed to en-GB here rather
          than taken from the browser: a date read as DD/MM/YYYY on one machine
          and mm/dd/yyyy on another is a date the user cannot trust. */}
      <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="en-gb">
        <TextFormatterProvider>
          <CssBaseline />
          <AuthProvider>
            <AttributesProvider>
              <DashboardProvider>
                <RouterProvider router={router} />
                <UpdateAvailableSnackbar />
                <DocsHandoffSnackbar />
              </DashboardProvider>
            </AttributesProvider>
          </AuthProvider>
        </TextFormatterProvider>
      </LocalizationProvider>
    </MuiThemeProvider>
  );
}

function App() {
  return (
    <ThemeProvider>
      <AppContent />
    </ThemeProvider>
  );
}

export default App;
